import { supabase } from "@/integrations/supabase/client";
import { convertImageToWebP } from "@/lib/image";

// ============================================================
// Anexos de Evento — upload, listagem e remoção de materiais para download.
// Spec: specs/event-attachments/{requirements,design,tasks}.md
//
// Compressão SEMPRE no client antes do upload (imagem→WebP, PDF→pdf-lib optimize).
// Limite de 10 MB POR EVENTO (soma dos tamanhos finais comprimidos).
// Download é público e direto do CDN (bucket público). A escrita na tabela de
// metadados só acontece via edge function manage-event-attachment (service role).
// ============================================================

export const EVENT_ATTACHMENTS_BUCKET = "event-attachments";
export const MAX_EVENT_BYTES = 10 * 1024 * 1024; // 10 MB por evento

// Allowlist de mime types (spec). Mantida em sincronia com a edge function.
export const ALLOWED_ATTACHMENT_MIME = new Set<string>([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
  "application/vnd.ms-excel", // xls
  "text/csv",
  "application/vnd.oasis.opendocument.spreadsheet", // ods
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
  "application/msword", // doc
  "application/vnd.oasis.opendocument.text", // odt
  "text/plain", // txt
  "application/rtf", // rtf
  "text/rtf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
  "application/vnd.ms-powerpoint", // ppt
  "application/vnd.oasis.opendocument.presentation", // odp
  "application/zip",
  "application/x-zip-compressed",
]);

// Denylist de extensões executáveis (rejeição forçada, mesmo se o mime "passar").
export const DENIED_ATTACHMENT_EXT = new Set<string>([
  "exe", "bat", "cmd", "com", "msi", "scr", "vbs", "vbe", "js", "jse",
  "ws", "wsf", "ps1", "psm1", "sh", "bash", "jar", "app", "dll", "deb",
  "rpm", "apk", "dmg", "pkg", "bin", "run", "gadget", "cpl", "hta", "reg",
]);

// Atributo accept do input file.
export const ATTACHMENT_ACCEPT_ATTR =
  ".pdf,.png,.jpg,.jpeg,.webp,.gif,.xlsx,.xls,.csv,.ods,.docx,.doc,.odt,.txt,.rtf,.pptx,.ppt,.odp,.zip";

export interface EventAttachment {
  id: string;
  event_id: string;
  storage_path: string;
  file_name: string;
  file_size_bytes: number;
  mime_type: string;
  sort_order: number;
  // Não é selecionado em leituras públicas (coluna revogada de anon/authenticated);
  // presente apenas no retorno da edge function (service role).
  uploaded_by?: string | null;
  created_at: string;
}

// Colunas públicas (sem uploaded_by, que é revogado de anon/authenticated no banco).
const PUBLIC_ATTACHMENT_COLUMNS =
  "id, event_id, storage_path, file_name, file_size_bytes, mime_type, sort_order, created_at";

function fileExtension(name: string): string {
  const parts = name.split(".");
  if (parts.length < 2) return "";
  return (parts.pop() || "").toLowerCase();
}

function isImage(file: File): boolean {
  return file.type.startsWith("image/") && file.type !== "image/gif";
}

function isPdf(file: File): boolean {
  return file.type === "application/pdf" || fileExtension(file.name) === "pdf";
}

export function isAllowedAttachment(file: File): boolean {
  const ext = fileExtension(file.name);
  if (DENIED_ATTACHMENT_EXT.has(ext)) return false;
  if (file.type && ALLOWED_ATTACHMENT_MIME.has(file.type)) return true;
  // Alguns navegadores não preenchem mime para csv/zip/ods etc.: cai para a extensão.
  return [
    "pdf", "png", "jpg", "jpeg", "webp", "gif", "xlsx", "xls", "csv", "ods",
    "docx", "doc", "odt", "txt", "rtf", "pptx", "ppt", "odp", "zip",
  ].includes(ext);
}

/**
 * Comprime o arquivo no client:
 *   - imagem (exceto gif) → WebP (canvas, lib/image.ts), fica com o menor.
 *   - PDF → pdf-lib optimize (useObjectStreams), fica com o menor.
 *   - outros → original.
 * Falhas de compressão degradam para o arquivo original (nunca quebram o upload).
 */
export async function compressAttachment(file: File): Promise<File> {
  if (isImage(file)) {
    try {
      const webp = await convertImageToWebP(file, {
        quality: 0.8,
        maxWidth: 2000,
        maxHeight: 2000,
      });
      return webp.size < file.size ? webp : file;
    } catch (err) {
      console.warn("[event-attachments] compressão de imagem falhou:", err);
      return file;
    }
  }

  if (isPdf(file)) {
    try {
      // Import dinâmico: PDF é o único caminho que usa pdf-lib.
      const { PDFDocument } = await import("pdf-lib");
      const bytes = new Uint8Array(await file.arrayBuffer());
      const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const optimized = await doc.save({ useObjectStreams: true });
      if (optimized.byteLength < bytes.byteLength) {
        return new File([optimized], file.name, { type: "application/pdf" });
      }
      return file;
    } catch (err) {
      console.warn("[event-attachments] otimização de PDF falhou, usando original:", err);
      return file;
    }
  }

  return file;
}

/** Soma o total de bytes já usados por um evento (sobre os tamanhos finais). */
export async function getEventAttachmentsUsage(eventId: string): Promise<number> {
  const { data, error } = await supabase
    .from("event_attachments")
    .select("file_size_bytes")
    .eq("event_id", eventId);
  if (error) throw error;
  return (data || []).reduce(
    (sum, r: { file_size_bytes: number }) => sum + (r.file_size_bytes || 0),
    0,
  );
}

/** Lista os anexos de um evento (SELECT público). */
export async function listEventAttachments(eventId: string): Promise<EventAttachment[]> {
  const { data, error } = await supabase
    .from("event_attachments")
    .select(PUBLIC_ATTACHMENT_COLUMNS)
    .eq("event_id", eventId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []) as unknown as EventAttachment[];
}

/** URL pública (CDN) de um anexo, para download direto. */
export function getAttachmentPublicUrl(storagePath: string): string {
  const { data } = supabase.storage
    .from(EVENT_ATTACHMENTS_BUCKET)
    .getPublicUrl(storagePath);
  return data.publicUrl;
}

/**
 * Valida → comprime → re-valida tamanho/total → upload ao Storage → invoca a
 * edge function de registro (que revalida com service role + magic-bytes).
 * Se o registro falhar, faz rollback do objeto no Storage.
 */
export async function uploadEventAttachment(params: {
  eventId: string;
  file: File;
}): Promise<EventAttachment> {
  const { eventId, file } = params;

  if (!isAllowedAttachment(file)) {
    throw new Error("Tipo de arquivo não permitido. Executáveis são bloqueados.");
  }

  const finalFile = await compressAttachment(file);

  if (finalFile.size <= 0) {
    throw new Error("Arquivo inválido (vazio).");
  }
  if (finalFile.size > MAX_EVENT_BYTES) {
    throw new Error("Arquivo muito grande. O limite é 10 MB por evento.");
  }

  // Checa o total atual do evento (UX; a edge fn é a autoridade final).
  const used = await getEventAttachmentsUsage(eventId);
  if (used + finalFile.size > MAX_EVENT_BYTES) {
    const restante = Math.max(0, MAX_EVENT_BYTES - used);
    throw new Error(
      `Limite de 10 MB por evento excedido. Espaço disponível: ${formatBytes(restante)}.`,
    );
  }

  const ext = fileExtension(finalFile.name) || fileExtension(file.name) || "bin";
  const storagePath = `events/${eventId}/${crypto.randomUUID()}.${ext}`;
  // mime real do arquivo (vai para os metadados / ícone da UI).
  const declaredMime = finalFile.type || file.type || "application/octet-stream";

  // O bucket é PÚBLICO e servido inline pelo CDN. Para que o browser NUNCA
  // renderize um anexo como markup (XSS), o objeto é armazenado com um
  // content-type não-renderável; o download usa o atributo `download`.
  // Imagens mantêm o tipo de imagem (para preview), o resto vira octet-stream.
  const storageContentType = declaredMime.startsWith("image/")
    ? declaredMime
    : "application/octet-stream";

  const { error: uploadErr } = await supabase.storage
    .from(EVENT_ATTACHMENTS_BUCKET)
    .upload(storagePath, finalFile, {
      contentType: storageContentType,
      upsert: false,
    });
  if (uploadErr) throw uploadErr;

  const { data, error } = await supabase.functions.invoke("manage-event-attachment", {
    body: {
      action: "register",
      eventId,
      storagePath,
      fileName: file.name, // nome original (não o .webp gerado)
      fileSizeBytes: finalFile.size,
      mimeType: declaredMime,
    },
  });

  if (error || !data?.attachment) {
    // Rollback do objeto se o registro falhou.
    await supabase.storage.from(EVENT_ATTACHMENTS_BUCKET).remove([storagePath]).catch(() => {});
    const message =
      (data && (data.error || data.message)) ||
      (error as Error | null)?.message ||
      "Falha ao registrar o anexo.";
    throw new Error(message);
  }

  return data.attachment as EventAttachment;
}

/** Remove um anexo (edge function: ownership → remove objeto + deleta linha). */
export async function deleteEventAttachment(attachmentId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke("manage-event-attachment", {
    body: { action: "delete", attachmentId },
  });
  if (error || !data?.ok) {
    const message =
      (data && (data.error || data.message)) ||
      (error as Error | null)?.message ||
      "Falha ao remover o anexo.";
    throw new Error(message);
  }
}

export function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Nome curto de ícone/categoria por mime, para a UI escolher o ícone. */
export function fileCategoryFor(mime: string): "pdf" | "image" | "sheet" | "doc" | "slides" | "zip" | "file" {
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("image/")) return "image";
  if (
    mime.includes("spreadsheet") || mime === "text/csv" ||
    mime === "application/vnd.ms-excel"
  ) {
    return "sheet";
  }
  if (
    mime.includes("presentation") || mime === "application/vnd.ms-powerpoint"
  ) {
    return "slides";
  }
  if (mime.includes("zip")) return "zip";
  if (
    mime.includes("word") || mime.includes("opendocument.text") ||
    mime === "text/plain" || mime.includes("rtf")
  ) {
    return "doc";
  }
  return "file";
}
